/**
 * BookingsPage Component
 *
 * Lists all user bookings with tab filtering.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import { DashboardLayout, BookingCard } from './components';
import { useMyBookings, BookingFilter } from './hooks';
import type { BookingWithListing } from './hooks/useMyBookings';

export const BookingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as BookingFilter) || 'upcoming';
  const [activeTab, setActiveTab] = useState<BookingFilter>(initialTab);
  const [cancelTarget, setCancelTarget] = useState<BookingWithListing | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const { bookings, isLoading, stats, refetch } = useMyBookings(activeTab);

  const handleTabChange = (tab: BookingFilter) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      const fn = httpsCallable<
        { bookingId: string; action: 'cancel'; reason?: string },
        { success: boolean }
      >(functions, 'respondToBooking');
      await fn({
        bookingId: cancelTarget.id,
        action: 'cancel',
        reason: cancelReason.trim() || undefined,
      });
      toast.success('Booking cancelled');
      setCancelTarget(null);
      setCancelReason('');
      refetch();
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to cancel booking. Try again.';
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const tabs: { key: BookingFilter; label: string; count?: number }[] = [
    { key: 'upcoming', label: 'Upcoming', count: stats.upcoming },
    { key: 'past', label: 'Past' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'all', label: 'All', count: stats.total },
  ];

  return (
    <DashboardLayout title="My Bookings">
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-32" />
          ))}
        </div>
      ) : bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              onCancel={setCancelTarget}
            />
          ))}
        </div>
      ) : (
        <EmptyState tab={activeTab} />
      )}

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cancel this booking?
            </h3>
            <p className="text-sm text-gray-600 mb-1">
              {cancelTarget.listing.title}
            </p>
            {cancelTarget.paymentStatus === 'paid' ? (
              <p className="text-sm text-gray-500 mb-4">
                You paid ${cancelTarget.pricing.totalAmount.toLocaleString()}.
                Your refund follows the listing's cancellation policy and our
                team reviews it within 24 hours. Card refunds land in 5-10
                business days once approved.
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                This booking hasn't been paid yet, so no refund is needed.
              </p>
            )}
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setCancelTarget(null);
                  setCancelReason('');
                }}
                disabled={isCancelling}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Keep booking
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={isCancelling}
                className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// Empty State
const EmptyState: React.FC<{ tab: BookingFilter }> = ({ tab }) => {
  const config = {
    upcoming: {
      icon: <CalendarIcon className="w-12 h-12" />,
      title: 'No upcoming trips',
      description: 'Time to plan your next adventure! Browse properties and book your stay.',
    },
    past: {
      icon: <ClockIcon className="w-12 h-12" />,
      title: 'No past trips',
      description: 'Your completed trips will appear here.',
    },
    cancelled: {
      icon: <XIcon className="w-12 h-12" />,
      title: 'No cancelled bookings',
      description: 'You have no cancelled bookings.',
    },
    all: {
      icon: <BookIcon className="w-12 h-12" />,
      title: 'No bookings yet',
      description: 'Start exploring properties and make your first booking!',
    },
  };

  const { icon, title, description } = config[tab];

  return (
    <div className="text-center py-16 px-4">
      <div className="text-gray-300 mb-4 flex justify-center">{icon}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 max-w-sm mx-auto">{description}</p>
    </div>
  );
};

// Icons
const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const BookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

export default BookingsPage;
